import _ from "lodash";
import React, { useState, useMemo, useEffect, useRef } from "react";
import classes from "./Calculator.scss";
import {
  Button,
  EmptyState,
  IconButton,
  SegmentControl,
  Tabs,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  CopyOutlined,
  DotsSixVerticalOutlined,
  ArrowBottomPaneOutlined,
  WarningTriangleColored,
} from "@toddle-edu/ds-icons";
import { MathEquationMenu } from "./config";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";

import { generateRandomId, getBasicDeviceInfo } from "Utils";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import MathLive, { MATHFIELD_ID, MAX_EQUATION_WIDTH } from "./MathLive";
import { WarningIllustration } from "@toddle-edu/ds-theme";
import classNames from "classnames";
import html2canvas from "html2canvas";
import { copyImageToClipboard } from "Worksheet/modules/WorksheetUtils";
import {
  isValidAssignVariable,
  getCalculatorInputTabs,
  sanitizeLatexInput,
  roundOffNumericalOutput,
} from "./CalculatorUtils";
import {
  CALCULATOR_UNITS,
  UNIT_DEGREES,
  UNIT_RADIANS,
} from "./CalculatorConstants";

const existingScript = document.getElementById("mathlive-script");

const loadMathLiveScripts = callback => {
  if (!existingScript) {
    const script = document.createElement("script");

    script.src = "/mathlive/mathlive-0.107.0.min.js";
    script.id = "mathlive-script";
    script.defer = 1;
    document.head.appendChild(script);
    script.onload = () => {
      if (callback) callback();
    };
  }
  if (existingScript && callback) callback();
};

const loadComputeScripts = callback => {
  if (!existingScript) {
    const script2 = document.createElement("script");

    script2.src = "/computeengine/compute-engine.min.js";
    script2.id = "compute-engine-script";
    script2.defer = 1;
    document.head.appendChild(script2);
    script2.onload = () => {
      if (callback) callback();
    };
  }
  if (existingScript && callback) callback();
};

const Calculator = ({
  setShowCalculator,
  setToastMsg,
  t,
  handleMouseDown,
  handleTouchStart,
  isStudentWorksheetWebView,
  testId,
  worksheetId,
  allowCopyPaste = true,
}) => {
  const tabs = useRef(getCalculatorInputTabs(t));
  const [currentTab, setCurrentTab] = useState(_.first(tabs.current)?.value);

  const [_latex, _setLatex] = useState("");

  const [output, setOutput] = useState("");
  const [outputRadians, setOutputRadians] = useState("");

  const [shiftOn, setShiftOn] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const inputBarContainerRef = useRef(null);
  const calculationsContainerRefs = useRef({});
  const [showTooltip, setShowTooltip] = useState("");
  const [isMathliveScriptLoaded, setIsMathliveScriptLoaded] = useState(
    existingScript ? true : false
  );
  const [isEditorCompatible, setIsEditorCompatible] = useState(true);
  const [computeContainer, setComputeContainer] = useState(null);

  const [currentUnit, setCurrentUnit] = useState(UNIT_DEGREES);

  const [calculations, setCalculations] = useState([]);
  const [expandedView, setExpandedView] = useState(false);

  const [enter, setEnter] = useState("");
  const [copying, setCopying] = useState(false);

  const baseLineRef = useRef(null);
  const calcWorkspaceRef = useRef(null);

  const scrollToView = () => {
    if (baseLineRef.current) {
      baseLineRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  };

  const copyWorkspace = () => {
    const maxWidth = 600;
    if (calcWorkspaceRef) {
      setCopying(true);
      html2canvas(calcWorkspaceRef?.current).then(function (canvas) {
        if (canvas.width > maxWidth) {
          const scale = maxWidth / canvas.width;
          const newWidth = canvas.width * scale;
          const newHeight = canvas.height * scale;
          const resizedCanvas = document.createElement("canvas");
          resizedCanvas.width = newWidth;
          resizedCanvas.height = newHeight;
          const ctx = resizedCanvas.getContext("2d");
          ctx.drawImage(canvas, 0, 0, newWidth, newHeight);
          canvas = resizedCanvas;
        }
        canvas.toBlob(function (blob) {
          if (navigator.userAgent.indexOf("Firefox") != -1) {
            const imageUrl = URL.createObjectURL(blob);
            const image = new window.Image();
            image.src = imageUrl;
            copyImageToClipboard(image);
            setCopying(false);
          } else {
            navigator.clipboard
              .write([
                new ClipboardItem({
                  "image/png": blob,
                }),
              ])
              .then(
                function () {
                  setCopying(false);
                  setToastMsg({
                    msg: "toastMsgs:equation_copied_successfully",
                    type: "success",
                    position: "toast-bottom-left",
                  });
                },
                function (err) {
                  setCopying(false);
                  console.error("Could not copy image: ", err);
                }
              );
          }
        });
      });
    }
  };

  const updateCurrentUnit = params => {
    setCurrentUnit(params);
    inputBarContainerRef.current?.focus();
  };

  let timer;

  function isNumeric(str) {
    return (
      //scientific notation
      /^\d*\.?\d*$/.test(str) ||
      /^-\d+$/.test(str) ||
      str == "Infinity" ||
      str == "undefined" ||
      /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/.test(str)
    );
  }

  const toggleExpandedView = () => {
    setExpandedView(prev => !prev);
  };

  const closeCalculator = () => {
    setShowCalculator(false);
  };

  const handleInputContainerClick = () => {
    inputBarContainerRef.current?.focus();
  };

  const handleMathLiveChange = (e, calc) => {
    const updatedCalc = calculations.map(calcObj => {
      if (calcObj.id === calc.id) {
        calcObj.latex = e.target.value;
        calcObj.output = calculateOutput(calc.latex, UNIT_DEGREES);
        calcObj.outputRadians = calculateOutput(calc.latex, UNIT_RADIANS);
      }
      return calcObj;
    });

    setCalculations(updatedCalc);
  };

  const updateTabs = tabs => {
    setCurrentTab(tabs);
    inputBarContainerRef.current?.focus();
  };

  useEffect(() => {
    scrollToView();
  }, [enter]);

  useEffect(() => {
    const storedCalculations = sessionStorage.getItem(
      `calculations_${worksheetId}`
    );
    if (storedCalculations) {
      setCalculations(JSON.parse(storedCalculations));
    }
  }, [worksheetId]);

  useEffect(() => {
    sessionStorage.setItem(
      `calculations_${worksheetId}`,
      JSON.stringify(calculations)
    );
  }, [calculations, worksheetId]);

  useEffect(() => {
    const deviceInfo = getBasicDeviceInfo();
    const versionArray = deviceInfo?.browser?.version?.split(".");
    const name = deviceInfo?.browser?.name;

    let shouldLoadScripts = true;

    if (versionArray.length > 2 && name) {
      const major = parseInt(versionArray[0]);
      const minor = parseInt(versionArray[1]);

      switch (name.toLowerCase()) {
        case "chrome":
          if (major < 71) {
            shouldLoadScripts = false;
          }
          break;

        case "edge":
          if (major < 79) {
            shouldLoadScripts = false;
          }
          break;

        case "safari":
          if (major <= 12 && minor < 1) {
            shouldLoadScripts = false;
          }
          break;

        case "firefox [reality]":
        case "firefox":
          if (major < 65) {
            shouldLoadScripts = false;
          }
          break;

        case "opera":
        case "opera coast":
          if (major < 58) {
            shouldLoadScripts = false;
          }
          break;

        case "android browser":
          if (major < 71) {
            shouldLoadScripts = false;
          }
          break;

        case "samsung browser":
          if (major < 10) {
            shouldLoadScripts = false;
          }
          break;

        default:
          break;
      }
    }
    if (shouldLoadScripts) {
      loadMathLiveScripts(() => {
        setIsMathliveScriptLoaded(true);
      });

      loadComputeScripts(() => {
        const ce = window.ComputeEngine;
        const ComputeEngine = ce?.ComputeEngine;
        if (ComputeEngine) {
          const Ce = new ComputeEngine();
          Ce.assign("nPr(n,r)", "$$ n!/(n-r)! $$");
          Ce.assign("nCr(n,r)", "$$ n!/((n-r)!*(r!)) $$");

          setComputeContainer(Ce);
        }
      });
    } else {
      setIsEditorCompatible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keyup", testCapsLock);
    document.addEventListener("keydown", testCapsLock);

    function testCapsLock(event) {
      if (event.code === "CapsLock") {
        const isCapsLockOn = event.getModifierState("CapsLock");
        if (isCapsLockOn && !shiftOn) {
          setShiftOn(true);
          setCapsLockOn(true);
        } else {
          setShiftOn(false);
          setCapsLockOn(false);
        }
      }
    }

    return () => {
      document.removeEventListener("keyup", testCapsLock);
      document.removeEventListener("keydown", testCapsLock);
    };
  }, []);

  const toggleShiftState = () => {
    if (!capsLockOn) setShiftOn(shiftOn => !shiftOn);
    else
      setToastMsg({
        msg: "toastMsgs:capsLock_is_already_on",
        type: "warning",
        position: "toast-bottom-left",
      });
  };

  const handleBackspace = latex => {
    if (_.isEmpty(latex) && !_.isEmpty(calculations)) {
      _setLatex(_.get(_.last(calculations), "latex", ""));
      setOutput(_.get(_.last(calculations), "output", ""));
      setCalculations(prevCalculations => prevCalculations.slice(0, -1));
    }
  };

  const handleBackspaceCalc = (latex, id) => {
    if (_.isEmpty(latex) && !_.isEmpty(calculations)) {
      setCalculations(prevCalculations =>
        prevCalculations.filter(ca => {
          return ca.id !== id;
        })
      );
    }
  };

  // get prev answer
  function extractLastAns(calc) {
    let lastOutput = "";
    for (let i = calc.length - 1; i >= 0; i--) {
      const currentObject = calc[i];
      if (currentUnit == UNIT_DEGREES) {
        if (isNumeric(currentObject.output)) {
          lastOutput = currentObject.output;
          break;
        }
      } else {
        if (isNumeric(currentObject.outputRadians)) {
          lastOutput = currentObject.outputRadians;
          break;
        }
      }
    }

    return String(lastOutput);
  }

  const onKeyboardButtonClick = btnObj => {
    const { shiftLatex, onClick } = btnObj;
    let { latex } = btnObj;

    const mainInput = document.getElementById(MATHFIELD_ID);

    //on key press ans
    if (btnObj.id === "ans") {
      latex = extractLastAns(calculations);
    }
    //on key press backspace
    if (btnObj.id === "backspace") {
      handleBackspace(_latex);
    }

    if (btnObj.id === "C") {
      _setLatex("");
      setOutput("");
      setOutputRadians("");
    }

    if (inputBarContainerRef?.current) {
      const { width } = inputBarContainerRef.current.getBoundingClientRect();

      if (onClick) {
        onClick(mainInput, toggleShiftState);
        if (btnObj.id === "AC") {
          setCalculations([]);
          _setLatex("");
          setOutput("");
          setOutputRadians("");
          mainInput.executeCommand("deleteAll");
          computeContainer?.popScope();
          computeContainer?.assign("nPr(n,r)", "$$ n!/(n-r)! $$");
          computeContainer?.assign("nCr(n,r)", "$$ n!/((n-r)!*(r!)) $$");
          computeContainer?.pushScope();
        }
      } else if (width < MAX_EQUATION_WIDTH) {
        mainInput.executeCommand([
          "insert",
          shiftOn && shiftLatex ? shiftLatex : latex,
          { insertionMode: "replaceSelection", selectionMode: "placeholder" },
        ]);
      } else {
        clearTimeout(timer);
        timer = setTimeout(
          () => setToastMsg("toastMsgs:equation_width_limit_reached"),
          500
        );
      }

      mainInput?.focus();
    }
  };

  const buttonsArr = useMemo(() => {
    const currentNode = MathEquationMenu["nodes"].find(node => {
      return node.id === currentTab;
    });

    const newButtonsArr = currentNode.children.map(child => {
      return child.map(nodeChild => {
        return (
          MathEquationMenu["nodeChildren"].find(
            buttonObj => buttonObj.id === nodeChild
          ) ||
          MathEquationMenu["commonChildren"].find(
            buttonObj => buttonObj.id === nodeChild
          )
        );
      });
    });

    return newButtonsArr;
  }, [
    currentTab,
    MathEquationMenu["nodes"],
    MathEquationMenu["nodeChildren"],
    MathEquationMenu["commonChildren"],
  ]);

  const buttonsCtrlArr = useMemo(() => {
    const currentNode = MathEquationMenu["nodes"].find(node => {
      return node.id === "headerCtrl";
    });
    const newButtonsArr = currentNode.children.map(nodeChild => {
      return (
        MathEquationMenu["nodeChildren"].find(
          buttonObj => buttonObj.id === nodeChild
        ) ||
        MathEquationMenu["commonChildren"].find(
          buttonObj => buttonObj.id === nodeChild
        )
      );
    });

    return newButtonsArr;
  }, [
    MathEquationMenu["nodes"],
    MathEquationMenu["nodeChildren"],
    MathEquationMenu["commonChildren"],
  ]);

  const ctrlContainer = classNames({
    [classes.main]: currentTab == "MAIN",
    [classes.abc]: currentTab == "ABC",
    [classes.func]: currentTab == "FUNC",
  });

  const buttonContainer = classNames({
    [classes.leftInnerButtonsContainer]: currentTab !== "ABC",
    [classes.leftInnerButtonsContainerRow]: currentTab === "ABC",
  });

  const calcContainer = classNames({
    [classes.calculationContainer]: !expandedView,
    [classes.calculationContainerExpand]: expandedView,
  });

  const computeLatex = (latex, currentUnit = UNIT_RADIANS) => {
    try {
      if (!computeContainer) {
        return "error";
      }

      computeContainer.angularUnit = currentUnit;
      let output = computeContainer.parse(latex).N().value;

      if (_.isNumber(output)) {
        output = roundOffNumericalOutput(output);
      }

      return output;
    } catch (error) {
      return "error";
    }
  };

  const onChangeLatex = e => {
    let inputLatex = e.target.value;

    try {
      _setLatex(inputLatex);

      inputLatex = sanitizeLatexInput(inputLatex);

      if (!_.includes(computeContainer?.parse(inputLatex).N().latex, "error")) {
        if (inputLatex.includes("=")) {
          const replacedString = inputLatex.replace(/\\;/g, "");

          const [key, value] = replacedString.split("=");

          if (isValidAssignVariable(key)) {
            inputLatex = value;
          } else {
            setOutput("error");
            setOutputRadians("error");
          }
        }

        const radiansOutput = computeLatex(inputLatex, UNIT_RADIANS);
        const degreesOutput = computeLatex(inputLatex, UNIT_DEGREES);

        setOutput(degreesOutput);
        setOutputRadians(radiansOutput);
      } else {
        setOutput("error");
        setOutputRadians("error");
      }
    } catch {
      // If computation fails, show warning icon in output
      setOutput("error");
      setOutputRadians("error");
      return;
    }
  };

  const calculateOutput = (latex, currentUnit) => {
    let inputLatex = sanitizeLatexInput(latex);

    if (!_.includes(computeContainer?.parse(inputLatex).N().latex, "error")) {
      if (inputLatex.includes("=")) {
        const replacedString = inputLatex.replace(/\\;/g, "");

        const [key, value] = replacedString.split("=");

        if (isValidAssignVariable(key)) {
          computeContainer?.assign(key, computeContainer?.parse(value));

          const mfe = document.getElementById(MATHFIELD_ID);
          mfe.setValue(_latex);

          inputLatex = value;
        } else {
          return "error";
        }
      }

      return computeLatex(inputLatex, currentUnit);
    } else {
      return "error";
    }
  };

  const handleCopy = latex => {
    navigator.clipboard.writeText(latex);
    setToastMsg({
      msg: "toastMsgs:equation_copied_successfully",
      type: "success",
      position: "toast-bottom-left",
    });
  };

  const handleEnter = (latex, isOnClick) => {
    const mfe = document.getElementById(MATHFIELD_ID);

    if (!_.isEmpty(latex)) {
      if (latex.includes("=")) {
        const replacedString = latex.replace(/\\;/g, "");
        const [key, value] = replacedString.split("=");
        if (isValidAssignVariable(key)) {
          computeContainer?.assign(key, computeContainer?.parse(value));
        }
      }

      setCalculations(prevCalculations => [
        ...prevCalculations,
        {
          id: generateRandomId(),
          latex,
          output,
          outputRadians,
        },
      ]);
      mfe.executeCommand("deleteAll");
      _setLatex("");
      setOutput("");
      setOutputRadians("");

      if (isOnClick) {
        setEnter(latex);
        mfe?.focus?.();
        mfe?.click?.();
      } else {
        scrollToView();
      }
    }
  };

  const normalizedOutput = currentUnit == UNIT_DEGREES ? output : outputRadians;

  const renderButtons = (buttonObj, ind) => {
    const Icon =
      shiftOn && buttonObj.shiftIcon ? buttonObj.shiftIcon : buttonObj.icon;

    let timer;

    //Time of the long press
    const TEMPO = 400;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    const mouseDown = () => {
      timer = setTimeout(function () {
        setShowTooltip(buttonObj.id);
      }, TEMPO);
    };

    const mouseUp = () => {
      setShowTooltip("");
      clearTimeout(timer);
    };

    if (
      (!isMobile && buttonObj.text) ||
      (buttonObj.text && showTooltip == buttonObj.id)
    ) {
      return (
        <Tooltip
          tooltip={buttonObj.text ?? buttonObj.id}
          key={buttonObj.id === "blank" ? `blank_${ind}` : buttonObj.id}
          visible={showTooltip == buttonObj.id}
          overlayInnerStyle={{
            padding: "6px 10px",
          }}
          align={{
            offset: [0, -5],
          }}
        >
          <Button
            id={buttonObj.id}
            size={"large"}
            dsVersion="2.0"
            variant={buttonObj.variant}
            type={buttonObj.type}
            _legacy_variant={buttonObj._legacy_variant}
            style={buttonObj.style ?? {}}
            onClick={() => onKeyboardButtonClick(buttonObj)}
            isFullWidth
            disabled={buttonObj.id === "blank"}
            onTouchEnd={mouseUp}
            onTouchStart={mouseDown}
            testId={`${testId}-calculator-onKeyboardButtonClick-withTooltip`}
          >
            {buttonObj.id === "blank" ? <div /> : Icon ? <Icon /> : <div />}
          </Button>
        </Tooltip>
      );
    } else {
      return (
        <Button
          id={buttonObj.id}
          size={"large"}
          dsVersion="2.0"
          variant={buttonObj.variant}
          type={buttonObj.type}
          _legacy_variant={buttonObj._legacy_variant}
          style={buttonObj.style ?? {}}
          onClick={() => onKeyboardButtonClick(buttonObj)}
          isFullWidth
          disabled={buttonObj.id === "blank"}
          className={buttonObj.className ?? ""}
          onTouchEnd={mouseUp}
          onTouchStart={mouseDown}
          testId={`${testId}-calculator-onKeyboardButtonClick-withoutTooltip`}
        >
          {buttonObj.id === "blank" ? <div /> : Icon ? <Icon /> : <div />}
        </Button>
      );
    }
  };

  const renderCalculator = () => {
    return isMathliveScriptLoaded ? (
      <div className={classes.scientificCalc}>
        <div className={classes.topPart}>
          <div className={classes.header}>
            <div className={classes.headerSpace}></div>
            <div className={classes.draggableHandler}>
              <IconButton
                icon={<DotsSixVerticalOutlined />}
                dsVersion="2.0"
                _legacy_size="small"
                _legacy_variant="plain"
                size="medium"
                variant="neutral"
                type="plain"
                className="handle"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                testId={`${testId}-calculator-handleTouchStart`}
              />
            </div>
            <div className={classes.header}>
              {!isStudentWorksheetWebView && allowCopyPaste ? (
                <IconButton
                  icon={<CopyOutlined />}
                  dsVersion="2.0"
                  _legacy_size="small"
                  _legacy_variant="plain"
                  size="medium"
                  variant="neutral"
                  type="plain"
                  className="handle"
                  onClick={copyWorkspace}
                  disabled={copying}
                  testId={`${testId}-calculator-copyWorkspace`}
                />
              ) : null}

              <IconButton
                icon={<ArrowBottomPaneOutlined />}
                dsVersion="2.0"
                _legacy_size="small"
                _legacy_variant="plain"
                size="medium"
                variant="neutral"
                type="plain"
                onClick={toggleExpandedView}
                testId={`${testId}-calculator-toggleExpandedView`}
              />
              <IconButton
                icon={<CloseOutlined />}
                dsVersion="2.0"
                _legacy_size="small"
                _legacy_variant="plain"
                size="medium"
                variant="neutral"
                type="plain"
                onClick={closeCalculator}
                testId={`${testId}-calculator-closeCalculator`}
              />
            </div>
          </div>
          <div className={calcContainer}>
            <div ref={calcWorkspaceRef} className={classes.containerScroll}>
              {calculations.map(calc => {
                const normalizedCalculation =
                  currentUnit == UNIT_DEGREES
                    ? calc.output
                    : calc.outputRadians;

                return (
                  <div
                    key={calc.id}
                    className={classes.calculatorInputContainer}
                    data-test-id={`${testId}-calculator-handleCalcMathLiveFocus`}
                  >
                    <div className={classes.mathLiveContainer}>
                      <MathLive
                        value={calc.latex}
                        onChange={e => handleMathLiveChange(e, calc)}
                        shiftOn={shiftOn}
                        mathLiveFieldContainerRef={ref => {
                          calculationsContainerRefs.current[calc?.id] = ref;
                        }}
                        timer={timer}
                        hideBorder={true}
                        nonEditable={true}
                        mathFieldId={calc.id}
                        handleBackspace={handleBackspaceCalc}
                        customProps={{ "read-only": true }}
                      />
                    </div>
                    {calc.output !== "" ? (
                      <div className={classes.outputContainer}>
                        {isNumeric(normalizedCalculation) &&
                        !_.isEmpty(calc.latex) ? (
                          <div className={classes.equalIcon}>=</div>
                        ) : (
                          ""
                        )}

                        <div className={classes.output}>
                          {isNumeric(normalizedCalculation) ? (
                            <p>{normalizedCalculation}</p>
                          ) : (
                            <div className={classes.warningIcon}>
                              <WarningTriangleColored
                                variant={"on"}
                                size={"x-small"}
                              />
                            </div>
                          )}
                        </div>

                        {calc.output !== "" &&
                        isNumeric(calc.output) &&
                        !_.isEmpty(calc.latex) &&
                        allowCopyPaste ? (
                          <CopyOutlined
                            size="xxx-small"
                            className={classes.copyIcon}
                            onClick={() => handleCopy(normalizedCalculation)}
                            testId={`${testId}-calculator-handleCopy`}
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              <div
                onClick={handleInputContainerClick}
                onKeyDown={e =>
                  onEnterOrSpaceKeyDown(e, handleInputContainerClick)
                }
                role="button"
                tabIndex={0}
                className={classes.calculatorInputContainer}
                data-test-id={`${testId}-calculator-inputField`}
              >
                <div ref={baseLineRef} className={classes.mathLiveContainer}>
                  <MathLive
                    value={_latex}
                    onChange={onChangeLatex}
                    shiftOn={shiftOn}
                    mathLiveFieldContainerRef={inputBarContainerRef}
                    timer={timer}
                    hideBorder={true}
                    handleEnter={handleEnter}
                    handleBackspace={handleBackspace}
                    mathFieldId={MATHFIELD_ID}
                  />
                </div>
                {output !== "" ? (
                  <div className={classes.outputContainer}>
                    {/* Empty check */}
                    {isNumeric(normalizedOutput) && !_.isEmpty(_latex) ? (
                      <div className={classes.equalIcon}>=</div>
                    ) : (
                      ""
                    )}

                    {/* Current Output */}
                    <div className={classes.output}>
                      {isNumeric(normalizedOutput) && !_.isEmpty(_latex) ? (
                        <p>{normalizedOutput}</p>
                      ) : (
                        <div className={classes.warningIcon}>
                          <WarningTriangleColored
                            variant={"on"}
                            size={"x-small"}
                          />
                        </div>
                      )}
                    </div>

                    {/* Copy option */}
                    {output !== "" &&
                    isNumeric(output) &&
                    !_.isEmpty(_latex) &&
                    allowCopyPaste ? (
                      <CopyOutlined
                        size="xxx-small"
                        className={classes.copyIcon}
                        onClick={() => handleCopy(normalizedOutput)}
                        testId={`${testId}-calculator-handleCopy`}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div
            className={
              expandedView ? classes.buttonsEnclosed : classes.buttonsContainer
            }
          >
            <div className={classes.tabsContainer}>
              <Tabs
                options={tabs.current}
                value={currentTab}
                onChange={newTabValue => updateTabs(newTabValue)}
                type="flexible"
                theme="light"
              />

              <div className={classes.unitContainer}>
                <SegmentControl
                  options={CALCULATOR_UNITS}
                  value={currentUnit}
                  size="medium"
                  onChange={updateCurrentUnit}
                  variant="text"
                />
              </div>
              <div className={classes.headerCtrlContainer}>
                {buttonsCtrlArr.map((buttonObj, ind) => {
                  return renderButtons(buttonObj, ind);
                })}
              </div>
            </div>
            <div className={classes.innerButtonsContainer}>
              <div className={buttonContainer}>
                {buttonsArr.map((val, i) => {
                  return (
                    <div key={generateRandomId()} className={ctrlContainer}>
                      {val.map((buttonObj, ind) => {
                        return buttonObj.id === "insert" ? (
                          <Button
                            key={buttonObj.id}
                            dsVersion="2.0"
                            variant={buttonObj.variant}
                            type={buttonObj.type}
                            _legacy_variant={buttonObj._legacy_variant}
                            className={classes.insertButton}
                            style={
                              currentTab == "FUNC"
                                ? { height: "48px" }
                                : buttonObj.style ?? {}
                            }
                            onClick={() => handleEnter(_latex, true)}
                            isFullWidth={true}
                            disabled={_latex === ""}
                            testId={`${testId}-calculator-handleEnter`}
                          >
                            {t(`common:${buttonObj.text}`)}
                          </Button>
                        ) : (
                          renderButtons(buttonObj, ind)
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <Loading containerStyle={{ height: "100%", padding: "1rem" }} />
    );
  };

  return isEditorCompatible ? (
    renderCalculator()
  ) : (
    <EmptyState
      illustration={WarningIllustration}
      primaryButton={
        <Button
          onClick={closeCalculator}
          testId={`${testId}-calculator-closeCalculator`}
        >
          {t("common:close")}
        </Button>
      }
      dsVersion="2.0"
      size="large"
      title={t("equationEditor:equation_editor_not_compatible")}
      titleStyle={{}}
    />
  );
};

const mapActionCreators = {
  setToastMsg,
};

export default compose(
  I18nHOC({ resource: ["common", "equationEditor"] }),
  connect(null, mapActionCreators)
)(Calculator);
